import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Receipt, Users, Package, Truck, ShoppingCart, ClipboardList, AlertTriangle, CheckCircle, Clock, TrendingUp, Activity } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import axios from 'axios';
import { API } from '../App';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    quotes: 0,
    invoices: 0,
    contacts: 0,
    clients: 0,
    suppliers: 0,
    products: 0,
    purchaseOrders: 0,
    assets: 0,
    defects: 0,
    serviceReports: 0
  });
  
  const [assetsByCompliance, setAssetsByCompliance] = useState([]);
  const [defectsBySeverity, setDefectsBySeverity] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [upcomingServices, setUpcomingServices] = useState([]);

  useEffect(() => {
    fetchStats();
  }, []); // eslint-disable-line

  const fetchStats = async () => {
    const token = localStorage.getItem('token');
    const config = {
      headers: { Authorization: `Bearer ${token}` }
    };

    // Fetch each endpoint individually with error handling to prevent cascade failures
    const fetchEndpoint = async (url, defaultValue = []) => {
      try {
        const response = await axios.get(url, config);
        return response.data;
      } catch (error) {
        console.error(`Failed to fetch ${url}:`, error);
        return defaultValue;
      }
    };

    try {
      const [quotes, invoices, contacts, clients, suppliers, products, pos, assets, defects, reports] = await Promise.all([
        fetchEndpoint(`${API}/quotes`),
        fetchEndpoint(`${API}/invoices`),
        fetchEndpoint(`${API}/contacts`),
        fetchEndpoint(`${API}/clients`),
        fetchEndpoint(`${API}/suppliers`),
        fetchEndpoint(`${API}/products`),
        fetchEndpoint(`${API}/purchase-orders`),
        fetchEndpoint(`${API}/assets`),
        fetchEndpoint(`${API}/defects`),
        fetchEndpoint(`${API}/service-reports`)
      ]);

      setStats({
        quotes: quotes.length,
        invoices: invoices.length,
        contacts: contacts.length,
        clients: clients.length,
        suppliers: suppliers.length,
        products: products.length,
        purchaseOrders: pos.length,
        assets: assets.length,
        defects: defects.filter(d => d.status === 'Open' || d.status === 'In Progress').length,
        serviceReports: reports.length
      });
      
      // Process assets by compliance status
      const complianceGroups = assets.reduce((acc, asset) => {
        acc[asset.compliance_status] = (acc[asset.compliance_status] || 0) + 1;
        return acc;
      }, {});
      
      setAssetsByCompliance([
        { name: 'Compliant', value: complianceGroups['Compliant'] || 0, color: '#10b981' },
        { name: 'Non-Compliant', value: complianceGroups['Non-Compliant'] || 0, color: '#ef4444' },
        { name: 'Requires Attention', value: complianceGroups['Requires Attention'] || 0, color: '#f59e0b' }
      ]);
      
      // Process defects by severity
      const severityGroups = defects.reduce((acc, defect) => {
        acc[defect.severity] = (acc[defect.severity] || 0) + 1;
        return acc;
      }, {});
      
      setDefectsBySeverity([
        { name: 'Critical', value: severityGroups['Critical'] || 0, color: '#dc2626' },
        { name: 'High', value: severityGroups['High'] || 0, color: '#f97316' },
        { name: 'Medium', value: severityGroups['Medium'] || 0, color: '#eab308' },
        { name: 'Low', value: severityGroups['Low'] || 0, color: '#3b82f6' }
      ]);
      
      // Get recent service reports (last 5)
      setRecentReports(reports.slice(0, 5));
      
      // Get assets with upcoming services (next 30 days)
      const today = new Date();
      const thirtyDaysLater = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      const upcoming = assets.filter(asset => {
        if (asset.next_service_due) {
          const dueDate = new Date(asset.next_service_due);
          return dueDate >= today && dueDate <= thirtyDaysLater;
        }
        return false;
      });
      setUpcomingServices(upcoming);
      
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const statCards = [
    {
      title: 'Total Assets',
      value: stats.assets,
      icon: Package,
      color: 'text-blue-400',
      bg: 'bg-blue-500/20'
    },
    {
      title: 'Open Defects',
      value: stats.defects,
      icon: AlertTriangle,
      color: 'text-red-400',
      bg: 'bg-red-500/20'
    },
    {
      title: 'Service Reports',
      value: stats.serviceReports,
      icon: ClipboardList,
      color: 'text-green-400',
      bg: 'bg-green-500/20'
    },
    {
      title: 'Active Clients',
      value: stats.clients,
      icon: Users,
      color: 'text-purple-400',
      bg: 'bg-purple-500/20'
    },
    {
      title: 'Total Quotes',
      value: stats.quotes,
      icon: FileText,
      color: 'text-orange-400',
      bg: 'bg-orange-500/20'
    },
    {
      title: 'Total Invoices',
      value: stats.invoices,
      icon: Receipt,
      color: 'text-yellow-400',
      bg: 'bg-yellow-500/20'
    }
  ];

  const quickActions = [
    { title: 'Create Quote', desc: 'Generate a new quote for clients', icon: FileText, path: '/admin/quotes' },
    { title: 'Create Invoice', desc: 'Generate a new invoice', icon: Receipt, path: '/admin/invoices' },
    { title: 'Create Purchase Order', desc: 'Order products from suppliers', icon: ShoppingCart, path: '/admin/purchase-orders' },
    { title: 'Add Client', desc: 'Register a new client', icon: Users, path: '/admin/clients' },
    { title: 'Add Supplier', desc: 'Add a new supplier', icon: Truck, path: '/admin/suppliers' },
    { title: 'Add Product', desc: 'Add product to catalog', icon: Package, path: '/admin/products' }
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard" className="space-y-8">
        <div>
          <h1 className="text-4xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-gray-400">Welcome back, {localStorage.getItem('username')}</p>
        </div>

        {/* Key Metrics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} data-testid={`stat-card-${index}`} className="bg-white/5 backdrop-blur-sm border-white/10 hover:bg-white/10 transition-all">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-gray-300">
                    {stat.title}
                  </CardTitle>
                  <div className={`${stat.bg} p-2 rounded-lg`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stat.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Analytics Charts */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Assets by Compliance */}
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-orange-500" />
                Assets by Compliance Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              {assetsByCompliance.length > 0 ? (
                <div>
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={assetsByCompliance}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {assetsByCompliance.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="mt-4 space-y-2">
                    {assetsByCompliance.map((entry, index) => (
                      <div key={index} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></div>
                          <span className="text-gray-300">{entry.name}</span>
                        </div>
                        <span className="text-white font-semibold">{entry.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  No asset data available
                </div>
              )}
            </CardContent>
          </Card>

          {/* Defects by Severity */}
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Defects by Severity
              </CardTitle>
            </CardHeader>
            <CardContent>
              {defectsBySeverity.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={defectsBySeverity}>
                    <XAxis dataKey="name" stroke="#9ca3af" />
                    <YAxis stroke="#9ca3af" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                    />
                    <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                      {defectsBySeverity.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-64 flex items-center justify-center text-gray-400">
                  No defect data available
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upcoming Services & Recent Reports */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Upcoming Services */}
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-yellow-500" />
                Upcoming Services (Next 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {upcomingServices.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {upcomingServices.slice(0, 5).map((asset, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex-1">
                        <p className="text-white font-medium">{asset.asset_type}</p>
                        <p className="text-sm text-gray-400">{asset.client_name} - {asset.site_name}</p>
                      </div>
                      <Badge className="bg-yellow-500">{asset.next_service_due}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  No upcoming services in the next 30 days
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Service Reports */}
          <Card className="bg-white/5 backdrop-blur-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <ClipboardList className="w-5 h-5 text-green-500" />
                Recent Service Reports
              </CardTitle>
            </CardHeader>
            <CardContent>
              {recentReports.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {recentReports.map((report, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-all cursor-pointer" onClick={() => navigate('/admin/service-reports')}>
                      <div className="flex-1">
                        <p className="text-white font-medium">{report.report_number}</p>
                        <p className="text-sm text-gray-400">{report.client_name} - {report.service_date}</p>
                      </div>
                      <Badge className={
                        report.overall_status === 'Pass' ? 'bg-green-500' :
                        report.overall_status === 'Pass with Defects' ? 'bg-yellow-500' :
                        'bg-red-500'
                      }>
                        {report.overall_status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  No service reports yet
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="bg-white/5 backdrop-blur-sm border-white/10">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {quickActions.map((action, index) => {
                const Icon = action.icon;
                return (
                  <button
                    key={index}
                    onClick={() => navigate(action.path)}
                    className="p-6 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-left group transition-all"
                  >
                    <Icon className="w-8 h-8 text-orange-500 mb-3 group-hover:scale-110 transition-transform" />
                    <h3 className="text-lg font-semibold text-white mb-1">{action.title}</h3>
                    <p className="text-sm text-gray-300">{action.desc}</p>
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;